import { useState, useEffect, useRef } from 'react';
import { useStoreState } from 'easy-peasy';
import client from '../../../../utils/apolloClient';
import { searchCompaniesQuery } from '../services/companySummaryQueries';

const LIST_SIZE = 20;
const THROTTLE_MS = 500;

const useSearchCompanies = () => {
  const { userId } = useStoreState(state => state.auth);

  const [companies, setCompanies] = useState([]);
  const [totalSize, setTotalSize] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [nameSearch, setNameSearch] = useState('');
  const [placeIdFilter, setPlaceIdFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [stateFilter, setStateFilter] = useState('');
  const [abnStatusFilter, setAbnStatusFilter] = useState('');
  const [bankStatusFilter, setBankStatusFilter] = useState('');
  const [sort, setSort] = useState('UPDATED_ASC');
  const [pageFrom, setPageFrom] = useState(0);
  const [currentPage, setCurrentPage] = useState(0);
  const [refetchKey, setRefetchKey] = useState(0);

  const requestId = useRef(0);
  const throttleTimer = useRef(null);
  const lastExecuted = useRef(null);

  const refresh = () => setRefetchKey(k => k + 1);

  useEffect(() => {
    if (!userId) return;

    const executeQuery = () => {
      const currentId = ++requestId.current;
      lastExecuted.current = Date.now();

      const company_filter = {};
      const place_filter = {};
      if (nameSearch) company_filter.name = nameSearch;
      if (placeIdFilter) place_filter.place_id = placeIdFilter;
      if (statusFilter) company_filter.status = statusFilter;
      if (stateFilter) company_filter.state = stateFilter;
      if (abnStatusFilter) company_filter.abn_status = abnStatusFilter;
      if (bankStatusFilter) company_filter.bank_status = bankStatusFilter;

      setLoading(true);
      client.clientPrivate
        .query({
          query: searchCompaniesQuery,
          variables: {
            input: {
              user_id: userId,
              size: LIST_SIZE,
              from: pageFrom,
              sort,
              filter: { company_filter, place_filter },
            },
          },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          if (currentId !== requestId.current) return;
          const result = data.search_companies;
          setCompanies(result.company_listing || []);
          setTotalSize(result.total_size);
          setTotalPages(result.total_pages);
          setLoading(false);
        })
        .catch(err => {
          if (currentId !== requestId.current) return;
          setError(err);
          setLoading(false);
        });
    };

    const elapsed = lastExecuted.current
      ? Date.now() - lastExecuted.current
      : Infinity;

    if (throttleTimer.current) clearTimeout(throttleTimer.current);

    if (elapsed >= THROTTLE_MS) {
      executeQuery();
    } else {
      throttleTimer.current = setTimeout(executeQuery, THROTTLE_MS - elapsed);
    }

    return () => {
      if (throttleTimer.current) clearTimeout(throttleTimer.current);
    };
  }, [
    userId,
    nameSearch,
    placeIdFilter,
    statusFilter,
    stateFilter,
    abnStatusFilter,
    bankStatusFilter,
    sort,
    pageFrom,
    refetchKey,
  ]);

  const resetPage = () => {
    setPageFrom(0);
    setCurrentPage(0);
  };

  return {
    companies,
    totalSize,
    totalPages,
    loading,
    error,
    listSize: LIST_SIZE,
    nameSearch,
    setNameSearch: val => {
      setNameSearch(val);
      resetPage();
    },
    placeIdFilter,
    setPlaceIdFilter: val => {
      setPlaceIdFilter(val);
      resetPage();
    },
    statusFilter,
    setStatusFilter: val => {
      setStatusFilter(val);
      resetPage();
    },
    stateFilter,
    setStateFilter: val => {
      setStateFilter(val);
      resetPage();
    },
    abnStatusFilter,
    setAbnStatusFilter: val => {
      setAbnStatusFilter(val);
      resetPage();
    },
    bankStatusFilter,
    setBankStatusFilter: val => {
      setBankStatusFilter(val);
      resetPage();
    },
    sort,
    setSort,
    pageFrom,
    setPageFrom,
    currentPage,
    setCurrentPage,
    refresh,
  };
};

export default useSearchCompanies;
